import React from 'react';
import { Table, Typography } from 'components';
import { EyeOutlined } from '@ant-design/icons';
import { screamingSnakeToTitleCase } from 'utils/helpers';

const { Text } = Typography;

interface HistoricalDataTableProps {
  data: any[];
  hoveredItemKey: string | null;
  onRowHover: (key: string | null) => void;
  onItemClick: (item: any) => void;
  getItemKey: (item: any) => string;
}

export const HistoricalDataTable: React.FC<HistoricalDataTableProps> = ({
  data,
  hoveredItemKey,
  onRowHover,
  onItemClick,
  getItemKey,
}) => {
  const columns = [
    {
      title: 'Date & Time',
      dataIndex: 'fullDate',
      key: 'fullDate',
      width: '35%',
      render: (fullDate: string, record: any) => {
        const isHovered = hoveredItemKey === getItemKey(record);
        return (
          <span
            style={{
              fontWeight: isHovered ? 'bold' : 'normal',
              color: isHovered ? '#047857' : '#333',
              transition: 'all 0.2s ease',
            }}
          >
            {fullDate}
          </span>
        );
      },
    },
    {
      title: 'Price',
      dataIndex: 'formattedPrice',
      key: 'price',
      width: '20%',
      render: (formattedPrice: string, record: any) => {
        const isHovered = hoveredItemKey === getItemKey(record);
        return (
          <span
            style={{
              fontWeight: isHovered ? 'bold' : 'normal',
              color: isHovered ? '#047857' : '#333',
              transition: 'all 0.2s ease',
            }}
          >
            {formattedPrice}
          </span>
        );
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: '30%',
      render: (source: string, record: any) => {
        const isHovered = hoveredItemKey === getItemKey(record);
        return (
          <span
            style={{
              fontWeight: isHovered ? 'normal' : 'normal',
              color: isHovered ? '#047857' : '#666',
              transition: 'all 0.2s ease',
            }}
          >
            {screamingSnakeToTitleCase(source)}
          </span>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: '15%',
      render: (_: any, record: any) => {
        const hasValidNavigation = record.supplierOfferUuid || record.sourceId;
        const isZeroPrice = record.price === 0;
        const isDisabled = !hasValidNavigation && !isZeroPrice;
        const isHovered = hoveredItemKey === getItemKey(record);

        return (
          <EyeOutlined
            style={{
              fontSize: '16px',
              color: isDisabled ? '#d9d9d9' : isHovered ? '#047857' : '#05796B',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              transform: isHovered ? 'scale(1.1)' : 'scale(1)',
            }}
            onClick={() => !isDisabled && onItemClick(record)}
          />
        );
      },
    },
  ];

  return (
    <div style={{ marginTop: '24px' }}>
      <Text strong className="font-size-16" style={{ marginBottom: '12px', display: 'block' }}>
        Historical Data Details
      </Text>
      <Table
        size="small"
        dataSource={data?.filter((item) => item.price > 0) || []}
        columns={columns}
        pagination={false}
        scroll={{ y: 200 }}
        rowKey={(record) => `${record.date}-${record.price}`}
        onRow={(record) => ({
          onMouseEnter: () => onRowHover(getItemKey(record)),
          onMouseLeave: () => onRowHover(null),
          style: {
            backgroundColor: hoveredItemKey === getItemKey(record) ? '#f0fdf4' : 'transparent',
            transition: 'background-color 0.2s ease',
          },
        })}
      />
    </div>
  );
};
